import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button, TextField, Typography, useIsMounted, useOverlayContext } from 'seagull';
import commonStyles from '~/components/ChangeEmailAddressModal/ChangeEmailAddress.module.scss';
import { ViewType, changeEmailErrorMessages, changeEmailSuccessMessages } from '~/components/ChangeEmailAddressModal/ChangeEmailAddressTypes';
import OTPInputSection from '~/components/ChangeEmailAddressModal/OTPInputSection';
import { useIoContext } from '~/contexts';
import { useSendOTPCode } from '~/hooks/useSendOTPCode/useSendOTPCode';
import { useLocaleKeys } from '~/i18n';
import { ServerError } from '~/infra/ErrorResponse';
import { handleRequest } from '~/infra/utils';
import { useStoreActions } from '~/store/store-context';
const VerifyNewEmail = (param)=>{
    let { handleClose, newUserEmail, handleSetView } = param;
    const { t: texts } = useLocaleKeys('components.changeEmailModal');
    const { setAuthToken, api } = useStoreActions();
    const isMounted = useIsMounted();
    const { sendOTPCode } = useSendOTPCode();
    const overlayContext = useOverlayContext();
    const { cookies } = useIoContext();
    const [isLoading, setIsLoading] = useState(false);
    const { control, handleSubmit, setError, reset, watch, clearErrors } = useForm({
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
        defaultValues: {
            otp: ''
        }
    });
    const handleChangeEmailAndAuthenticate = async ()=>{
        const authToken = cookies.getAuthToken();
        await setAuthToken(authToken);
        overlayContext.showSuccessToast(changeEmailSuccessMessages.emailChanged, 'email-verified-successfully-toast');
        handleClose();
        return;
    };
    const handleError = (error)=>{
        if (error.hasServerError(ServerError.InvalidOTP)) {
            setError('otp', {
                message: changeEmailErrorMessages.otpCodeIsInvalid
            });
            return;
        }
        if (error.hasServerError(ServerError.EmailAlreadyRegistered)) {
            setError('otp', {
                message: changeEmailErrorMessages.emailAlreadyRegistered
            });
            return;
        }
        if (error.isTooManyAttemptsError()) {
            handleSetView(ViewType.TooManyAttempts);
            return;
        }
        setError('otp', {
            message: changeEmailErrorMessages.somethingWentWrong
        });
    };
    const verifyEmailWithOTPCode = async (param)=>{
        let { otp } = param;
        setIsLoading(true);
        const changeEmailResult = await handleRequest(api.user.verifyEmailWithOTP({
            email: newUserEmail,
            otp: otp
        }));
        if (changeEmailResult.success) {
            await handleChangeEmailAndAuthenticate();
        } else {
            handleError(changeEmailResult.error);
        }
        if (isMounted()) {
            setIsLoading(false);
        }
    };
    const onSendOTPCode = async (param)=>{
        let { showSuccessToast } = param;
        await sendOTPCode({
            requestOTP: ()=>api.user.changeUserEmail({
                    email: newUserEmail
                }),
            onTooManyRequests: ()=>handleSetView(ViewType.TooManyAttempts),
            showSuccessToast
        });
    };
    const resendCode = async ()=>{
        reset();
        await onSendOTPCode({
            showSuccessToast: true
        });
    };
    const otpValue = watch('otp');
    useEffect(()=>{
        clearErrors('otp');
    }, [
        otpValue,
        clearErrors
    ]);
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": "verify-email-address-view",
        children: /*#__PURE__*/ _jsxs("form", {
            onSubmit: handleSubmit(verifyEmailWithOTPCode),
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: commonStyles.content,
                    children: [
                        /*#__PURE__*/ _jsxs(Typography, {
                            variant: "paragraph1",
                            testId: "verify-email-address-info-text",
                            children: [
                                "To verify your new email address, we’ll send you a confirmation email ",
                                /*#__PURE__*/ _jsx("br", {}),
                                " containing a one-time code."
                            ]
                        }),
                        /*#__PURE__*/ _jsx(TextField, {
                            bi: {
                                name: 'verify_new_email'
                            },
                            collapseHelperText: false,
                            width: 360,
                            testId: "verify-email-address",
                            type: "email",
                            label: texts.newEmailLabel(),
                            value: newUserEmail,
                            disabled: true
                        }),
                        /*#__PURE__*/ _jsx(OTPInputSection, {
                            isLoading: isLoading,
                            control: control,
                            resendCode: resendCode
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: commonStyles.footer,
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'cancel_verify_new_email'
                            },
                            onClick: handleClose,
                            skin: "primaryOutlineOpaque",
                            testId: "verify-email-address-cancel-button",
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'verify_new_email_otp'
                            },
                            type: "submit",
                            testId: "verify-otp-submit-button",
                            loading: isLoading,
                            children: "Verify code and update email"
                        })
                    ]
                })
            ]
        })
    });
};
export default VerifyNewEmail;
